import {
  type NavigateFunction,
  type SetURLSearchParams,
} from "react-router-dom";
import {
  setSilentEntryTransition,
} from "src/redux/slices/entrySlice";
import { setLatestEntryId } from "src/redux/slices/generatorSlice";
import type { AppDispatch } from "src/redux/store";
import { MEDLBID } from "src/utils";
import {
  resolveEntryId,
  type EntryIdFields,
} from "src/utils/logbookUtils/resolveEntryId";

export function clearNewSearchParam(setSearchParams: SetURLSearchParams) {
  setSearchParams(
    prev => {
      if (!prev.has("new")) return prev;
      const next = new URLSearchParams(prev);
      next.delete("new");
      return next;
    },
    { replace: true },
  );
}

export function navigateToLogbookEntry(
  navigate: NavigateFunction,
  setSearchParams: SetURLSearchParams,
  logbookId: string,
  idType: string,
  entryId: string,
) {
  const pathname = `/logbook/${logbookId}/${idType}/${entryId}`;

  navigate({ pathname, search: "" }, { replace: true });
  clearNewSearchParam(setSearchParams);
}

export function updateEntryRouteSilently(
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  setSearchParams: SetURLSearchParams,
  logbookId: string,
  idType: string,
  entryId: string,
) {
  dispatch(setSilentEntryTransition(true));
  dispatch(setLatestEntryId(entryId));
  navigate(
    { pathname: `/logbook/${logbookId}/${idType}/${entryId}`, search: "" },
    { replace: true },
  );
  clearNewSearchParam(setSearchParams);
}

export const prefetchLogbookEntry = async (
  logbookId: string,
  idType: string,
  entryId: string,
): Promise<boolean> => {
  if (!window?.api?.get) {
    return true;
  }

  try {
    const result = await window.api.get(
      "get-logbook",
      {
        _part: idType === "partid",
        params: {
          lbid: logbookId,
          [idType]: entryId,
        },
      },
      true,
    );
    const content = result?.data?.content;
    return (
      result?.success !== false &&
      content?.success !== false &&
      !!result?.data?.content?.data?.latest
    );
  } catch {
    return false;
  }
};

export const getLogbookIdType = (lb: { lbtype: string; value: string }) => {
  if (lb.lbtype === "part") return "partid";
  if (lb.value === MEDLBID) return "meid";
  return "lbeid";
};

const flattenHomeLogbooks = (homeData: {
  data?: { logbooks?: Record<string, unknown[]> };
}): Array<{ lbid: string; latest?: EntryIdFields | null }> => {
  const logs = homeData?.data?.logbooks;
  if (!logs || typeof logs !== "object") return [];

  const result: Array<{ lbid: string; latest?: EntryIdFields | null }> = [];
  for (const items of Object.values(logs)) {
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      if (item && typeof item === "object" && "logbooks" in item) {
        const grouped = item as { logbooks?: Array<{ lbid: string; latest?: EntryIdFields | null }> };
        if (Array.isArray(grouped.logbooks)) {
          result.push(...grouped.logbooks);
        }
      } else if (item && typeof item === "object" && "lbid" in item) {
        result.push(item as { lbid: string; latest?: EntryIdFields | null });
      }
    }
  }
  return result;
};

export const resolveLatestEntryIdForLogbook = async (
  lbid: string,
  _lbtype: string,
  latest?: EntryIdFields | null,
): Promise<string | null> => {
  const fromLatest = resolveEntryId(latest);
  if (fromLatest) return fromLatest;

  if (!window?.api?.get) return null;

  try {
    const home = await window.api.get("get-home", {}, true);
    const logbook = flattenHomeLogbooks(home).find(lb => lb.lbid === lbid);
    return resolveEntryId(logbook?.latest);
  } catch {
    return null;
  }
};

export const navigateToLogbookEntryAfterPrefetch = async (
  navigate: NavigateFunction,
  setSearchParams: SetURLSearchParams,
  logbookId: string,
  idType: string,
  entryId: string,
): Promise<boolean> => {
  const canNavigate = await prefetchLogbookEntry(logbookId, idType, entryId);
  if (!canNavigate) {
    return false;
  }
  navigateToLogbookEntry(navigate, setSearchParams, logbookId, idType, entryId);
  return true;
};
